import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { applyViewportFix } from './utils/viewportFix';
import { Helmet } from 'react-helmet-async';

// Import Hero component eagerly for better LCP performance
import Hero from './components/Hero';

// Import other critical components with priority
const Text = lazy(() => import('./components/Text'));
const Video = lazy(() => import('./components/Video'));
const CardHypnosis = lazy(() => import('./components/cardHypnosis'));

// Lazy load non-critical pages
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const AstroConsult = lazy(() => import('./pages/AstroConsult'));
const Hypnosis = lazy(() => import('./pages/Hypnosis'));

// Loading Component with skeleton UI
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#1a0b2e] to-[#271043]">
    <div className="w-full max-w-4xl mx-auto px-4">
      <div className="space-y-6">
        <div className="h-64 bg-white/5 rounded-lg animate-pulse" />
        <div className="h-32 bg-white/5 rounded-lg animate-pulse" />
      </div>
    </div>
  </div>
);

// App Content with optimized loading sequence
const AppContent = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#1a0b2e] to-[#271043]">
      <Helmet>
        <html lang="sr-RS" />
        <title>Miloš Živković Profa | Astrolog i Hipnoterapeut</title>
        <meta name="description" content="Miloš Živković Profa - profesionalni astrolog i hipnoterapeut sa dugogodišnjim iskustvom. Astrološke konsultacije, hipnoterapija i podcast. Saznajte više o astrologiji i hipnozi kroz moj jedinstveni pristup." />
        <link rel="canonical" href="https://miloszivkovic.com/" />
        <meta name="language" content="sr-RS" />
        {/* Video preloading is handled directly in the video element with preload attribute */}
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Miloš Živković Profa",
            "url": "https://miloszivkovic.com/",
            "description": "Miloš Živković Profa - profesionalni astrolog i hipnoterapeut sa dugogodišnjim iskustvom. Astrološke konsultacije, hipnoterapija i podcast.",
            "author": {
              "@type": "Person",
              "name": "Miloš Živković",
              "alternateName": "Profa",
              "url": "https://miloszivkovic.com/o-meni/"
            }
          }
        `}
        </script>
      </Helmet>
      <Header />
      <main className="flex-grow relative">
        <Routes>
          <Route
            path="/"
            element={
              <div className="bg-gradient-to-b from-[#1a0b2e] to-[#271043]">
                <Hero />
                <Suspense fallback={<div className="h-96 bg-transparent" />}>
                  <CardHypnosis />
                </Suspense>
                <Suspense fallback={<div className="h-96 bg-transparent" />}>
                  <Video />
                </Suspense>
                <Suspense fallback={<div className="h-96 bg-transparent" />}>
                  <Text />
                </Suspense>
              </div>
            }
          />
          <Route
            path="/o-meni/"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <div className="bg-gradient-to-b from-[#1a0b2e] to-[#271043]">
                  <About />
                </div>
              </Suspense>
            }
          />
          <Route
            path="/kontakt/"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <div className="bg-gradient-to-b from-[#1a0b2e] to-[#271043]">
                  <Contact />
                </div>
              </Suspense>
            }
          />
          <Route
            path="/astroloske-konsultacije/"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <div className="bg-gradient-to-b from-[#1a0b2e] to-[#271043]">
                  <AstroConsult />
                </div>
              </Suspense>
            }
          />
          <Route
            path="/hipnoze/"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <div className="bg-gradient-to-b from-[#1a0b2e] to-[#271043]">
                  <Hypnosis />
                </div>
              </Suspense>
            }
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

const App = () => {
  // Apply viewport fix for mobile devices
  useEffect(() => {
    applyViewportFix();
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
};

export default App;
